/**
 * BookmarkButton / BookmarkToast / BookmarkLoginBanner
 *
 * 全ページで使える汎用ブックマークボタン（Apple HIG準拠）。
 *
 * ログイン済み  → API で即時保存/解除 + トースト通知
 * 未ログイン   → pending-bookmarks に保存 → スティッキーバナーで後からログイン誘導
 *
 * signedIn は省略可。省略時は Inertia shared props の account.signedIn を参照する。
 */

import { Link, usePage } from "@inertiajs/react";
import { Bookmark, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  mypageBookmarksPath,
  mypagePendingBookmarksPath,
} from "@/routes/account";

interface SharedProps {
  account?: { signedIn?: boolean };
  [key: string]: unknown;
}

export interface BookmarkButtonProps {
  recordType: string;
  recordId: string;
  apiPath: string;
  initialBookmarked?: boolean;
  signedIn?: boolean;
  /** "light": 動画・暗背景上で使う半透明スタイル / "default": 明背景用 */
  variant?: "light" | "default";
  className?: string;
  // 後方互換のため残す（無視する）
  size?: "sm" | "md";
}

// ─── Pending bookmarks (未ログイン時のローカル保存) ──────────────────────────

const PENDING_KEY = "pending_bookmarks_v1";

type PendingEntry = { recordType: string; recordId: string };

export function readPending(): PendingEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writePending(entries: PendingEntry[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(PENDING_KEY, JSON.stringify(entries));
}

export function isPendingBookmarked(
  recordType: string,
  recordId: string,
): boolean {
  return readPending().some(
    (e) => e.recordType === recordType && e.recordId === recordId,
  );
}

export function addPending(entry: PendingEntry) {
  const current = readPending();
  const exists = current.some(
    (e) => e.recordType === entry.recordType && e.recordId === entry.recordId,
  );
  if (!exists) writePending([...current, entry]);
}

export function removePending(entry: PendingEntry) {
  writePending(
    readPending().filter(
      (e) =>
        !(e.recordType === entry.recordType && e.recordId === entry.recordId),
    ),
  );
}

export function removePendingByType(recordType: string) {
  writePending(readPending().filter((e) => e.recordType !== recordType));
}

export function pendingIdsForType(recordType: string): string[] {
  return readPending()
    .filter((e) => e.recordType === recordType)
    .map((e) => e.recordId);
}

export function clearPendingBookmarks() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PENDING_KEY);
}

const RECORD_TYPE_TO_PAYLOAD_KEY: Record<string, string> = {
  Style: "styles",
  Post: "posts",
  "Magazine::Article": "articles",
  article: "articles",
  Salon: "salons",
  Commitment: "commitments",
  Menu: "menus",
};

function base64UrlEncode(value: string): string {
  return window
    .btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

/** pending_bookmarks_v1 から全 record type を Base64 payload に変換する */
export function buildBookmarkPayload(): string | null {
  if (typeof window === "undefined") return null;
  const entries = readPending();
  if (entries.length === 0) return null;

  const grouped: Record<string, string[]> = {};
  for (const { recordType, recordId } of entries) {
    const key = RECORD_TYPE_TO_PAYLOAD_KEY[recordType] ?? recordType;
    (grouped[key] ??= []).push(recordId);
  }

  return base64UrlEncode(JSON.stringify(grouped));
}

// pending の変更を購読する仕組み
const pendingListeners = new Set<() => void>();
function notifyPendingChanged() {
  pendingListeners.forEach((fn) => fn());
}

function usePendingCount() {
  const [count, setCount] = useState(() => readPending().length);
  useEffect(() => {
    const update = () => setCount(readPending().length);
    pendingListeners.add(update);
    return () => {
      pendingListeners.delete(update);
    };
  }, []);
  return count;
}

// ─── Toast state ──────────────────────────────────────────────────────────────

type ToastState = { kind: "saved" } | { kind: "unsaved" } | null;

let toastTimeout: ReturnType<typeof setTimeout> | null = null;
const toastListeners = new Set<(state: ToastState) => void>();

function showToast(state: NonNullable<ToastState>, durationMs = 2500) {
  if (toastTimeout) clearTimeout(toastTimeout);
  toastListeners.forEach((fn) => fn(state));
  toastTimeout = setTimeout(() => {
    toastListeners.forEach((fn) => fn(null));
  }, durationMs);
}

function useToastState() {
  const [state, setState] = useState<ToastState>(null);
  useEffect(() => {
    toastListeners.add(setState);
    return () => {
      toastListeners.delete(setState);
    };
  }, []);
  return state;
}

// ─── Toast UI ─────────────────────────────────────────────────────────────────

export function BookmarkToast() {
  const state = useToastState();
  const visible = state !== null;

  const dismiss = () => {
    if (toastTimeout) clearTimeout(toastTimeout);
    toastListeners.forEach((fn) => fn(null));
  };

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className={[
        "fixed inset-x-0 bottom-0 z-[9998] flex justify-center px-4 pb-6 transition-all duration-300",
        visible
          ? "pointer-events-auto opacity-100 translate-y-0"
          : "pointer-events-none opacity-0 translate-y-2",
      ].join(" ")}
    >
      {state?.kind === "saved" && (
        <Link
          href={mypageBookmarksPath()}
          className="inline-flex items-center gap-3 rounded-2xl bg-gray-900/95 px-4 py-3 shadow-lg backdrop-blur-sm"
          onClick={dismiss}
        >
          <Bookmark className="h-4 w-4 shrink-0 fill-white text-white" />
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-white leading-tight">
              保存しました
            </span>
            <span className="text-xs text-white/60 leading-tight mt-0.5">
              マイページで確認できるよ →
            </span>
          </div>
        </Link>
      )}

      {state?.kind === "unsaved" && (
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-700/90 px-4 py-2.5 shadow-lg backdrop-blur-sm">
          <Bookmark className="h-3.5 w-3.5 text-white/60" strokeWidth={1.75} />
          <span className="text-sm font-medium text-white/90">
            保存を解除しました
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Login Banner (未ログイン時スティッキーフッター) ──────────────────────────

export function BookmarkLoginBanner() {
  const page = usePage<SharedProps>();
  const signedIn = !!page.props.account?.signedIn;
  const pendingCount = usePendingCount();
  const [dismissed, setDismissed] = useState(false);

  // ログイン済みになったら消す
  const visible = !signedIn && pendingCount > 0 && !dismissed;

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[9997]">
      <div
        className="mx-auto max-w-md overflow-hidden bg-gray-900 shadow-2xl"
        style={{ boxShadow: "0 4px 32px rgba(0,0,0,0.18)" }}
      >
        <div className="flex items-center gap-3 px-4 py-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10">
            <Bookmark className="h-4 w-4 fill-white text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white leading-tight">
              {pendingCount}件を保存中
            </p>
            <p className="text-xs text-white/60 leading-tight mt-0.5">
              ログインすると次回から確認できるよ
            </p>
          </div>
          <Link
            href={mypagePendingBookmarksPath()}
            className="shrink-0 rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition hover:bg-white/90 active:scale-95"
          >
            保存を見る
          </Link>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full text-white/40 transition hover:text-white/70"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── BookmarkButton ────────────────────────────────────────────────────────────

export function BookmarkButton({
  recordType,
  recordId,
  apiPath,
  initialBookmarked = false,
  signedIn: signedInProp,
  variant = "default",
  className,
}: BookmarkButtonProps) {
  const page = usePage<SharedProps>();
  const signedIn = signedInProp ?? !!page.props.account?.signedIn;

  // 未ログイン時: pending から初期状態を読む
  const [bookmarked, setBookmarked] = useState(() => {
    if (initialBookmarked) return true;
    if (typeof window === "undefined") return false;
    return readPending().some(
      (e) => e.recordType === recordType && e.recordId === recordId,
    );
  });
  const [submitting, setSubmitting] = useState(false);

  const persistBookmark = useCallback(
    async (next: boolean) => {
      const csrfToken = document
        .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.getAttribute("content");
      const body = JSON.stringify({
        record_type: recordType,
        record_id: recordId,
      });
      const res = await fetch(apiPath, {
        method: next ? "POST" : "DELETE",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body,
      });
      return res.ok;
    },
    [apiPath, recordType, recordId],
  );

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (submitting) return;

    const next = !bookmarked;
    setBookmarked(next);

    // 未ログイン → pending に保存してバナーで誘導
    if (!signedIn) {
      if (next) {
        addPending({ recordType, recordId });
      } else {
        removePending({ recordType, recordId });
      }
      notifyPendingChanged();
      return;
    }

    // ログイン済み → API + トースト
    setSubmitting(true);
    const ok = await persistBookmark(next).catch(() => false);
    if (ok) {
      showToast(next ? { kind: "saved" } : { kind: "unsaved" });
    } else {
      setBookmarked(!next);
    }
    setSubmitting(false);
  };

  const isLight = variant === "light";

  return (
    <div className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={submitting}
        aria-label={bookmarked ? "保存を解除する" : "保存する"}
        aria-pressed={bookmarked}
        className={[
          "inline-flex h-11 w-11 items-center justify-center rounded-full transition-all duration-150",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
          isLight
            ? bookmarked
              ? "text-white backdrop-blur-sm"
              : "text-white/70 hover:text-white backdrop-blur-sm"
            : bookmarked
              ? "text-gray-900"
              : "text-gray-400 hover:text-gray-700",
        ].join(" ")}
      >
        <Bookmark
          className={[
            "h-6 w-6 transition-all duration-150",
            bookmarked ? "fill-current" : "",
            isLight ? "drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]" : "",
          ].join(" ")}
          strokeWidth={1.5}
        />
      </button>
    </div>
  );
}
